import mustache from 'mustache';
import { readFile } from 'node:fs/promises';
import { EMBEDDED_SCRIPTS_DIR } from '@wix/cli-app-definitions';
import type { DevCenterComponent } from '../dev-center-component.js';
import { composeValidations, pathValidation } from './common.js';
import type { ValidationResult } from './common.js';

function getEmbeddedScriptFolderName(folder: string) {
  return folder
    .replace(`${EMBEDDED_SCRIPTS_DIR}/`, '')
    .replace('/embedded.html', '');
}

function embeddedScriptExistsValidation(
  existingScripts: Array<DevCenterComponent<'EmbeddedScript'>>
) {
  return (folder: string) =>
    existingScripts.every(
      (existingScript) =>
        folder !== getEmbeddedScriptFolderName(existingScript.relativePath)
    );
}

function embeddedScriptNameValidation(name: string) {
  return pathValidation(getEmbeddedScriptFolderName(name));
}

async function readTemplate(filepath: string) {
  const html = await readFile(filepath, 'utf-8');
  return mustache.parse(html).filter(([type]) => type === 'name');
}

export async function validateEmbeddedScriptHtml(
  path: string
): Promise<ValidationResult> {
  try {
    const template = await readTemplate(path);
    const params = template.filter(([type]) => type === 'name');

    const invalidParameters = params
      .filter(([, name]) => !/^[a-zA-Z]+$/.test(name))
      .map(([, name]) => name);

    if (invalidParameters.length > 0) {
      return {
        valid: false,
        reason: `Dynamic parameters can only include letters, but some include invalid names: ${invalidParameters.join(
          ', '
        )}`,
      };
    }

    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      reason:
        error instanceof Error
          ? error.message
          : 'Invalid embedded script template',
    };
  }
}

export function createEmbeddedScriptFolderValidation(
  embeddedScripts: Array<DevCenterComponent<'EmbeddedScript'>>
) {
  return composeValidations([
    [
      embeddedScriptExistsValidation(embeddedScripts),
      'The script folder must be unique, please choose a name that has not been used yet in your application.',
    ],
    [
      embeddedScriptNameValidation,
      'The script folder must be a valid path ([a-z0-9-/]+) and must not start with a dot.',
    ],
  ]);
}
